import React, { useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Stack,
  useTheme,
  alpha,
} from '@mui/material';
import { Image as ImageIcon, Upload, CloudUpload } from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useReceiptStore } from '@/store/receiptStore';
import { useUploadStore, generateUploadId } from '@/store/uploadStore';
import { useNotificationStore } from '@/store/notificationStore';
import {
  validateImageFormat,
  validateFileSize,
  createImagePreview,
  revokeImagePreview,
  reencodeImage,
} from '@/utils/imageUtils';
import { uploadReceipt, pollForProcessedReceipt } from '@/api/receipt';
import { pairReceiptToShoppingList } from '@/api/shoppingList';

interface LocationState {
  pairWithListId?: string;
}
import { ErrorAlert } from '@/components/ErrorAlert';
import { ImagePreview } from '@/components/ImagePreview';

export default function UploadPage() {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as LocationState | null;
  const pairWithListId = state?.pairWithListId;

  const {
    imageUri,
    error,
    setImageUri,
    setImageData,
    setReceiptData,
    setOriginalReceiptData,
    setError,
    reset: resetReceiptStore,
  } = useReceiptStore();
  const { startUpload, markProcessing, completeUpload, failUpload } = useUploadStore();
  const addNotification = useNotificationStore((state) => state.addNotification);
  const [isStartingUpload, setIsStartingUpload] = useState(false);

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);

  const processFile = useCallback((file: File) => {
    setError(null);

    if (!validateImageFormat(file)) {
      setError('Unsupported file format. Please select an image file');
      return;
    }

    if (!validateFileSize(file)) {
      setError('File size exceeds 5MB limit. Please select a smaller image');
      return;
    }

    // Create preview URL
    const previewUrl = createImagePreview(file);
    setImageUri(previewUrl);
    setSelectedFile(file);
  }, [setError, setImageUri]);

  const handleFileSelect = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    processFile(file);
  }, [processFile]);

  const handleDrop = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    setIsDragOver(false);

    const file = event.dataTransfer.files?.[0];
    if (file) {
      processFile(file);
    }
  }, [processFile]);

  const handleDragOver = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    setIsDragOver(false);
  }, []);

  const handleUpload = async () => {
    if (!selectedFile || !imageUri) {
      setError('Please select an image first');
      return;
    }

    setIsStartingUpload(true);
    setError(null);

    try {
      const reencoded = await reencodeImage(selectedFile);
      const imageData = new Uint8Array(await reencoded.arrayBuffer());
      setImageData(imageData);

      // Generate upload ID and start tracking
      const uploadId = generateUploadId();
      startUpload(uploadId, pairWithListId);

      // Navigate immediately - to lists if pairing, otherwise receipts list
      if (pairWithListId) {
        navigate('/lists');
      } else {
        navigate('/receipts');
      }

      // Process upload in background
      processUploadInBackground(uploadId, imageData, pairWithListId);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to prepare upload');
      setIsStartingUpload(false);
    }
  };

  const processUploadInBackground = async (uploadId: string, imageData: Uint8Array, listIdToPair?: string) => {
    try {
      // Upload image to API — returns immediately with receipt_id + PROCESSING status
      const uploadResponse = await uploadReceipt({ imageData });
      const receiptId = uploadResponse.receiptId;

      // Image is uploaded; now the worker processes it async
      markProcessing(uploadId, receiptId);

      // Poll GetReceipt until processing completes
      const result = await pollForProcessedReceipt(receiptId);

      setReceiptData(result as any);
      setOriginalReceiptData(result as any);

      // If we have a list to pair with, do the pairing
      if (listIdToPair) {
        try {
          await pairReceiptToShoppingList({
            listId: listIdToPair,
            receiptId,
          });
          completeUpload(uploadId, receiptId);
          addNotification({
            message: 'Receipt uploaded and linked to shopping list',
            severity: 'success',
            action: {
              label: 'View List',
              href: '/lists',
            },
          });
        } catch (pairErr) {
          // Processing succeeded but pairing failed
          completeUpload(uploadId, receiptId);
          addNotification({
            message: `Receipt uploaded but linking failed: ${pairErr instanceof Error ? pairErr.message : 'Unknown error'}`,
            severity: 'warning',
            action: {
              label: 'Review',
              href: `/receipt/${receiptId}`,
            },
          });
        }
      } else {
        completeUpload(uploadId, receiptId);
        addNotification({
          message: 'Receipt uploaded successfully',
          severity: 'success',
          action: {
            label: 'Review',
            href: `/receipt/${receiptId}`,
          },
        });
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Upload failed';
      failUpload(uploadId, errorMessage);
      addNotification({
        message: `Upload failed: ${errorMessage}`,
        severity: 'error',
        autoHideDuration: 8000,
      });
    } finally {
      resetReceiptStore();
      setSelectedFile(null);
      setIsStartingUpload(false);
    }
  };

  // Cleanup preview URL on unmount
  React.useEffect(() => {
    return () => {
      if (imageUri) {
        revokeImagePreview(imageUri);
      }
    };
  }, [imageUri]);

  return (
    <Box sx={{ py: 3, px: { xs: 2, md: 3 }, maxWidth: 800, mx: 'auto', overflowX: 'hidden' }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, color: 'text.primary' }}>
          {pairWithListId ? 'Upload Receipt for Shopping List' : 'Upload Receipt'}
        </Typography>
        <Typography variant="body1" color="text.secondary">
          {pairWithListId
            ? 'Upload a receipt image to link with your shopping list and track your actual purchases.'
            : 'Upload an image of your receipt to automatically extract data and track your spending.'}
        </Typography>
      </Box>

      <Box>
        <Stack spacing={3}>
          <Card
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            elevation={0}
            sx={{
              border: 2,
              borderColor: isDragOver ? 'primary.main' : 'divider',
              borderStyle: 'dashed',
              bgcolor: isDragOver ? alpha(theme.palette.primary.main, 0.05) : 'background.paper',
              transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
              cursor: !imageUri ? 'pointer' : 'default',
              borderRadius: 4,
              '&:hover': {
                borderColor: !imageUri ? 'primary.light' : 'divider',
                bgcolor: !imageUri ? alpha(theme.palette.primary.main, 0.02) : 'background.paper',
              },
            }}
          >
            {imageUri ? (
              <ImagePreview uri={imageUri} />
            ) : (
              <CardContent
                sx={{
                  py: 10,
                  textAlign: 'center',
                }}
                onClick={() => document.getElementById('file-input')?.click()}
              >
                <Box
                  sx={{
                    display: 'inline-flex',
                    p: 2,
                    borderRadius: '50%',
                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                    color: 'primary.main',
                    mb: 2,
                  }}
                >
                  <CloudUpload sx={{ fontSize: 40 }} />
                </Box>
                <Typography variant="h6" gutterBottom color="text.primary" fontWeight="600">
                  {isDragOver ? 'Drop it here' : 'Drag and drop your receipt'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  High quality images work best for data extraction
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
                  Supports JPEG, PNG, and WebP (max 5MB)
                </Typography>
              </CardContent>
            )}
          </Card>

          <Stack direction="row" spacing={2}>
            <Button
              variant="outlined"
              component="label"
              startIcon={<ImageIcon />}
              fullWidth
              sx={{ py: 1.5 }}
            >
              Choose Image
              <input
                id="file-input"
                type="file"
                accept="image/*"
                hidden
                onChange={handleFileSelect}
              />
            </Button>
          </Stack>

          <ErrorAlert message={error} onDismiss={() => setError(null)} />

          <Button
            variant="contained"
            size="large"
            onClick={handleUpload}
            disabled={!selectedFile || isStartingUpload}
            startIcon={<Upload />}
            fullWidth
            sx={{ py: 1.5, fontSize: '1.1rem' }}
          >
            {isStartingUpload ? 'Starting...' : 'Upload & Process'}
          </Button>
        </Stack>
      </Box>
    </Box>
  );
}
